import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Observable } from 'rxjs/internal/Observable';
import { takeUntil } from 'rxjs/operators';

import { BaseComponent, FormatService, ReportTableColumn, SummaryItem } from '../../../../../tms-shared';
import {
  OrderPropertyReport,
  PickingListReportLine,
  PickingListReportWarehouse,
  SalesOrderConfirmationReport,
  SalesOrderConfirmationReportLine,
  SalesOrderDeliveryReport,
  SalesOrderExtraReport,
  SalesOrderReportLineProcessing,
  SalesOrderVatAnalysis,
} from '../../../../models';
import { SalesOrderReportsService, SalesOrderService } from '../../../../services';

@Component({
  selector: 'app-sales-order-confirmation-report',
  templateUrl: './sales-order-confirmation-report.component.html',
  styleUrl: './sales-order-confirmation-report.component.scss',
})
export class SalesOrderConfirmationReportComponent extends BaseComponent implements OnInit {
  @Input() salesOrderHeaderId: string;

  model$: Observable<SalesOrderConfirmationReport>;
  showVatAnalysisOnDocuments: boolean;

  constructor(
    private salesOrderReportsService: SalesOrderReportsService,
    private salesOrderService: SalesOrderService,
    private formatService: FormatService,
    private route: ActivatedRoute,
  ) {
    super();
  }

  ngOnInit() {
    if (!this.salesOrderHeaderId) {
      this.salesOrderHeaderId = this.route.snapshot.paramMap.get('id');
    }
    this.getModel();

    this.salesOrderService.refreshDetails.pipe(takeUntil(this.destroy$)).subscribe(() => this.getModel());
  }

  private getModel() {
    this.model$ = this.salesOrderReportsService.getSalesOrderConfirmation(this.salesOrderHeaderId);
    this.model$
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (model: SalesOrderConfirmationReport) => (this.showVatAnalysisOnDocuments = model.showVatAnalysisOnDocuments),
      );
  }

  headerSummaryItems(model: SalesOrderConfirmationReport): SummaryItem[] {
    return [
      { label: $localize`Order Number`, value: model.orderNo },
      { label: $localize`Your Order Number`, value: model.customerOrderNo },
      { label: $localize`Your Order Reference`, value: model.customerOrderRef },
      { label: $localize`Customer Account`, value: model.customerKey },
      { label: $localize`Order Date`, value: this.formatService.date(model.dateOrdered) },
      { label: $localize`Operator`, value: model.enteredBy },
      { label: $localize`Ordered By`, value: model.orderedBy },
    ];
  }

  confirmationSummaryItems(model: SalesOrderConfirmationReport): SummaryItem[] {
    return model.customerConfirmationEmailAddress
      ? [{ label: $localize`Confirmation emailed to`, value: model.customerConfirmationEmailAddress }]
      : [];
  }

  lineColumnsWarehouse(model: PickingListReportWarehouse): ReportTableColumn[] {
    return [
      {
        label: $localize`Warehouse`,
        value: model.warehouse ? model.warehouse : $localize`Not Allocated`,
      },
    ];
  }

  lineSubTableColumnsWarehouse(model: PickingListReportLine): ReportTableColumn[] {
    return [
      {
        label: $localize`Product`,
        value: model.productKey,
      },
      {
        label: $localize`Description`,
        value: model.productDescription,
      },
      {
        label: $localize`Location`,
        value: model.location,
      },
      {
        label: $localize`Delivery Date`,
        value: model.deliveryDateString,
      },
      {
        label: $localize`Quantity`,
        value: model.quantityString,
        textRight: true,
      },
      {
        label: $localize`Price`,
        value: model.priceString,
        textRight: true,
      },
      {
        label: $localize`Value`,
        value: model.valueString,
        textRight: true,
      },
    ];
  }

  lineColumns(model: SalesOrderConfirmationReportLine): ReportTableColumn[] {
    return [
      {
        label: $localize`Product`,
        value: this.formatService.valuesToHtml(this.formatProductDescription(model)),
      },
      {
        label: $localize`Delivery Date`,
        value: this.formatService.valuesToHtml(this.formatDeliveryDate(model)),
      },
      {
        label: $localize`Quantity`,
        value: `${this.formatService.number(model.quantity)} ${model.unit}`,
        textRight: true,
      },
      {
        label: $localize`Price`,
        value: `${model.currencySymbol}${this.formatService.number(model.price)} ${model.priceListUnit}`,
        textRight: true,
      },
      {
        label: $localize`Disc %`,
        value: this.formatService.number(model.discount),
        textRight: true,
      },
      {
        label: $localize`Value`,
        value: this.formatService.currency(model.value, model.currencySymbol),
        textRight: true,
      },
    ];
  }

  lineSubProcessingTableColumns(model: SalesOrderReportLineProcessing): ReportTableColumn[] {
    return [
      { label: $localize`Process`, value: model.processProductKey },
      { label: $localize`Description`, value: model.processProductDescription },
      { label: $localize`Price`, value: `${model.currencySymbol}${model.price}`, textRight: true },
      { label: $localize`Value`, value: `${model.currencySymbol}${model.value}`, textRight: true },
    ];
  }

  deliveryColumns(model: SalesOrderDeliveryReport): ReportTableColumn[] {
    return [
      {
        label: $localize`Product`,
        value: model.productDescription,
      },
      {
        label: $localize`Notes`,
        value: this.formatService.multiLineToHtml(model.notes),
      },
      {
        label: $localize`Quantity`,
        value: this.formatService.number(model.quantity),
        textRight: true,
        widthSmall: true,
      },
      {
        label: $localize`Price`,
        value: this.formatService.currency(model.price, model.currencySymbol),
        textRight: true,
        widthSmall: true,
      },
      {
        label: $localize`Value`,
        value: this.formatService.currency(model.value, model.currencySymbol),
        textRight: true,
        widthSmall: true,
      },
    ];
  }

  extraColumns(model: SalesOrderExtraReport): ReportTableColumn[] {
    return [
      {
        label: $localize`Product`,
        value: model.productDescription,
      },
      {
        label: $localize`Notes`,
        value: this.formatService.multiLineToHtml(model.notes),
      },
      {
        label: $localize`Quantity`,
        value: this.formatService.number(model.quantity),
        textRight: true,
        widthSmall: true,
      },
      {
        label: $localize`Price`,
        value: this.formatService.currency(model.price, model.currencySymbol),
        textRight: true,
        widthSmall: true,
      },
      {
        label: $localize`Value`,
        value: this.formatService.currency(model.value, model.currencySymbol),
        textRight: true,
        widthSmall: true,
      },
    ];
  }

  vatAnalysis(model: SalesOrderVatAnalysis): ReportTableColumn[] {
    if (this.showVatAnalysisOnDocuments) {
      return [
        {
          label: '',
          value: '',
        },
        {
          label: $localize`Nett Value`,
          value: this.formatService.currency(model.nettValue, model.currencySymbol),
          textRight: true,
        },
        {
          label: $localize`VAT Rate`,
          value: this.formatService.number(model.taxRate) + '%',
          textRight: true,
        },
        {
          label: $localize`VAT Value`,
          value: this.formatService.currency(model.vat, model.currencySymbol),
          textRight: true,
        },
        {
          label: $localize`Overall Value`,
          value: this.formatService.currency(model.overallValue, model.currencySymbol),
          textRight: true,
        },
      ];
    } else {
      return [];
    }
  }

  additionalDetails(model: OrderPropertyReport[]): SummaryItem[] {
    return model.map((m: OrderPropertyReport) => ({
      label: m.heading,
      value: this.formatProperty(m),
    }));
  }

  footerSummaryItems(model: SalesOrderConfirmationReport): SummaryItem[][] {
    if (this.showVatAnalysisOnDocuments) {
      return [
        [
          { label: model.currency ? $localize`Currency` : '', value: model.currency },
          { label: model.agentName ? $localize`Agent` : '', value: model.agentName },
          { label: model.paymentTerms ? $localize`Payment Terms` : '', value: model.paymentTerms },
          { label: model.deliveryTerms ? $localize`Delivery Terms` : '', value: model.deliveryTerms },
          { label: model.deliveryTermNotes ? $localize`Delivery Term Notes` : '', value: model.deliveryTermNotes },
          { label: model.modeOfTransport ? $localize`Mode of Transport` : '', value: model.modeOfTransport },
          { label: model.shipper ? $localize`Shipper` : '', value: model.shipper },
          { label: model.shippingNotes ? $localize`Shipping Notes` : '', value: model.shippingNotes },
          { label: model.documentNotes ? $localize`Notes` : '', value: model.documentNotes },
          {
            label: model.deliveryInstructions ? $localize`Delivery Instructions` : '',
            value: model.deliveryInstructions,
          },
        ],
        [],
      ];
    } else {
      return [
        [
          { label: model.currency ? $localize`Currency` : '', value: model.currency },
          { label: model.agentName ? $localize`Agent` : '', value: model.agentName },
          { label: model.paymentTerms ? $localize`Payment Terms` : '', value: model.paymentTerms },
          { label: model.deliveryTerms ? $localize`Delivery Terms` : '', value: model.deliveryTerms },
          { label: model.deliveryTermNotes ? $localize`Delivery Term Notes` : '', value: model.deliveryTermNotes },
          { label: model.modeOfTransport ? $localize`Mode of Transport` : '', value: model.modeOfTransport },
          { label: model.shipper ? $localize`Shipper` : '', value: model.shipper },
          { label: model.shippingNotes ? $localize`Shipping Notes` : '', value: model.shippingNotes },
          { label: model.documentNotes ? $localize`Notes` : '', value: model.documentNotes },
          {
            label: model.deliveryInstructions ? $localize`Delivery Instructions` : '',
            value: model.deliveryInstructions,
          },
        ],
        [
          {
            label: $localize`Nett Total`,
            value: this.formatService.currency(model.nettValue, model.currencySymbol),
            textRight: true,
          },
          {
            label: $localize`VAT` + ' @ ' + (model.taxRate > 0 ? ` ${model.taxRate}%` : ''),
            value: this.formatService.currency(model.taxValue, model.currencySymbol),
            textRight: true,
          },
          {
            label: $localize`Overall Total`,
            value: `<strong><u>${this.formatService.currency(model.overallValue, model.currencySymbol)}</u></strong>`,
            textRight: true,
          },
        ],
      ];
    }
  }
  private formatProperty(model: OrderPropertyReport): string {
    return model.boolean ? model.trueString : model.displayText;
  }

  private formatProductDescription(model: SalesOrderConfirmationReportLine): string[] {
    const values = [model.productKey, model.productDescription];
    if (model.customerProduct) {
      values.push($localize`Customer Product` + ' ' + model.customerProduct);
    }
    if (model.customerLineRef) {
      values.push($localize`Order Reference` + ' ' + model.customerLineRef);
    }
    if (model.makeUp) {
      values.push($localize`Make Up` + ' ' + model.makeUp);
    }
    if (model.orderLineNotes) {
      values.push($localize`Line Notes` + ' ' + model.orderLineNotes);
    }
    if (model.yarnSpec?.colourDescription) {
      values.push($localize`Colour` + ' ' + model.yarnSpec?.colourDescription);
    }
    if (model.yarnSpec?.countDescription) {
      values.push($localize`Count` + ' ' + model.yarnSpec?.countDescription);
    }
    if (model.yarnSpec?.micron) {
      values.push($localize`Micron` + ' ' + model.yarnSpec?.micron);
    }
    if (model.contractNumber > 0) {
      values.push(
        $localize`From Contract` +
          ' ' +
          model.contractNumber +
          $localize` Balance remaining` +
          ' ' +
          this.formatService.number(model.contractBalance),
      );
    }
    if (model.returnMessage) {
      values.push(model.returnMessage);
    }

    return values;
  }

  private formatProductDescriptionWarehouse(model: PickingListReportLine): string[] {
    const values = [model.productKey, model.productDescription];
    if (model.customerLineRef) {
      values.push($localize`Order Reference` + ' ' + model.customerLineRef);
    }
    if (model.orderLineNotes) {
      values.push($localize`Line Notes` + ' ' + model.orderLineNotes);
    }

    return values;
  }

  private formatDeliveryDate(model: SalesOrderConfirmationReportLine): string[] {
    const values = [this.formatService.date(model.deliveryDate)];
    if (model.deliveryNotes) {
      values.push(model.deliveryNotes);
    }

    return values;
  }

  private formatDeliveryDateWarehouse(model: PickingListReportLine): string[] {
    const values = [this.formatService.date(model.deliveryDate)];
    if (model.deliveryNotes) {
      values.push(model.deliveryNotes);
    }

    return values;
  }
}
